define([
  'underscore',
  'axios',
], (_, axios) => {

  // Convert RGB to Hex
  function rgbToHex(rgb) {
    return `#${rgb.map((value) => {
      const x = value.toString(16);
      return (x.length === 1) ? `0${x}` : x;
    }).join('')}`;
  }

  // Convert Hex to RGB array
  function hexToRGB(hex) {
    // Expand shorthand form (e.g. "03F") to full form (e.g. "0033FF")
    const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
    const hexVal = hex.replace(shorthandRegex, (m, r, g, b) => r + r + g + g + b + b);
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hexVal);

    return result ? [
      parseInt(result[1], 16),
      parseInt(result[2], 16),
      parseInt(result[3], 16),
    ] : [0, 0, 0];
  }

  // Convert RGB string to RGB int array
  function rgbToArray(rgb) {
    let count = 4;
    if (rgb.charAt(3) === 'a') {
      count = 5;
    }
    const rgbArr = rgb.substring(count, rgb.length - 1)
      .replace(/ /g, '')
      .split(',')
      .map(Number);

    if (rgbArr.length === 4) {
      rgbArr.pop();
    }
    return rgbArr;
  }

  // Calculate luminance from rgb
  function luminance(rgb) {
    return (rgb[0] * 0.2126) + (rgb[1] * 0.7152) + (rgb[2] * 0.0722);
  }

  // Default light and dark color scheme
  const lightColor = {
    lum: luminance([225, 225, 225]),
    hex: '#E1E1E1',
    rgb: [225, 225, 225],
  };

  const darkColor = {
    lum: luminance([50, 50, 50]),
    hex: '#323232',
    rgb: [50, 50, 50],
  };

  function setColorTypes(color) {
    if (color.charAt(0) === '#') {
      const rgbArr = hexToRGB(color);
      return {
        lum: luminance(rgbArr),
        hex: color,
        rgb: rgbArr,
      };
    } else if (color.charAt(0) === 'R' || color.charAt(0) === 'r') {
      const rgbArr = rgbToArray(color);
      return {
        lum: luminance(rgbArr),
        hex: rgbToHex(rgbArr),
        rgb: rgbArr,
      };
    }
    return {
      lum: 0,
      hex: '#000000',
      rgb: [0, 0, 0],
    };
  }

  /**
   * Calculate Complementary Color
   * stefan-kendall@github
   * RGB2HSV and HSV2RGB are based on Color Match Remix [http://color.twysted.net/]
   * which is based on or copied from ColorMatch 5K [http://colormatch.dk/]
   */

  function HueShift(h, s) {
    let hue = h + s;
    while (hue >= 360.0) hue -= 360.0;
    while (hue < 0.0) hue += 360.0;
    return hue;
  }

  function min3(a, b, c) {
    return (a < b) ? ((a < c) ? a : c) : ((b < c) ? b : c);
  }
  function max3(a, b, c) {
    return (a > b) ? ((a > c) ? a : c) : ((b > c) ? b : c);
  }

  function RGB2HSV(rgb) {
    const hsv = {};
    const max = max3(rgb.r, rgb.g, rgb.b);
    const dif = max - min3(rgb.r, rgb.g, rgb.b);
    hsv.saturation = (max === 0.0) ? 0 : ((100 * dif) / max);
    if (hsv.saturation === 0) hsv.hue = 0;
    else if (rgb.r === max) hsv.hue = (60.0 * (rgb.g - rgb.b)) / dif;
    else if (rgb.g === max) hsv.hue = 120.0 + ((60.0 * (rgb.b - rgb.r)) / dif);
    else if (rgb.b === max) hsv.hue = 240.0 + ((60.0 * (rgb.r - rgb.g)) / dif);
    if (hsv.hue < 0.0) hsv.hue += 360.0;
    hsv.value = Math.round((max * 100) / 255);
    hsv.hue = Math.round(hsv.hue);
    hsv.saturation = Math.round(hsv.saturation);
    return hsv;
  }

  function HSV2RGB(val) {
    const hsv = val;
    const rgb = {};
    if (hsv.saturation === 0) {
      rgb.r = Math.round(hsv.value * 2.55);
      rgb.g = Math.round(hsv.value * 2.55);
      rgb.b = Math.round(hsv.value * 2.55);
    } else {
      hsv.hue /= 60;
      hsv.saturation /= 100;
      hsv.value /= 100;
      const i = Math.floor(hsv.hue);
      const f = hsv.hue - i;
      const p = hsv.value * (1 - hsv.saturation);
      const q = hsv.value * (1 - (hsv.saturation * f));
      const t = hsv.value * (1 - (hsv.saturation * (1 - f)));
      switch (i) {
        case 0: rgb.r = hsv.value; rgb.g = t; rgb.b = p; break;
        case 1: rgb.r = q; rgb.g = hsv.value; rgb.b = p; break;
        case 2: rgb.r = p; rgb.g = hsv.value; rgb.b = t; break;
        case 3: rgb.r = p; rgb.g = q; rgb.b = hsv.value; break;
        case 4: rgb.r = t; rgb.g = p; rgb.b = hsv.value; break;
        default: rgb.r = hsv.value; rgb.g = p; rgb.b = q;
      }
      rgb.r = Math.round(rgb.r * 255);
      rgb.g = Math.round(rgb.g * 255);
      rgb.b = Math.round(rgb.b * 255);
    }

    const rgbArr = [rgb.r, rgb.g, rgb.b];

    return {
      lum: luminance(rgbArr),
      hex: rgbToHex(rgbArr),
      rgb: rgbArr,
    };
  }

  // Calculate compliment color
  function getComplimentColor(rgb) {
    const temphsv = RGB2HSV({ r: rgb[0], g: rgb[1], b: rgb[2] });
    temphsv.hue = HueShift(temphsv.hue, 180.0);
    return HSV2RGB(temphsv);
  }

  // Calculate contrast ratio
  function contrast(lumHigh, lumLow) {
    return (lumHigh + 0.05) / (lumLow + 0.05);
  }

  // Calculate difference color (contrast) difference between two colors and return rgb array
  function calculateColorDiff(primaryColor, secondaryColor) {
    if (primaryColor.lum > secondaryColor.lum) {
      if (contrast(primaryColor.lum, secondaryColor.lum) > 1.8) {
        return primaryColor;
      }
      return lightColor;
    } else if (contrast(secondaryColor.lum, primaryColor.lum) > 1.8) {
      return primaryColor;
    }
    return darkColor;
  }

  // Format rgb array into correct format
  function formatColorRGBA(color) {
    return `rgba(${color.rgb[0]},${color.rgb[1]},${color.rgb[2]},${1})`;
  }

  // Find valid font backgrounds
  function correctedFontColor(font, background) {
    if (background.lum > font.lum) {
      if (contrast(background.lum, font.lum) >= 2.5) {
        return font;
      }
    } else if (contrast(font.lum, background.lum) >= 2.5) {
      return font;
    } else if (background.lum < 128) {
      return lightColor;
    }

    return darkColor;
  }

  // Create background color objects
  function backgroundColorValues(data) {
    const colorArray = [];
    if (data.primaryColor) {
      colorArray.push(setColorTypes(data.primaryColor));
    }
    if (data.secondaryColor) {
      colorArray.push(setColorTypes(data.secondaryColor));
    }
    if (data.tertiaryColor) {
      colorArray.push(setColorTypes(data.tertiaryColor));
    }
    return colorArray;
  }

  // Create font object
  function fontColorValue(font, backgroundColor) {
    if (font) {
      const rgbFont = hexToRGB(font);
      return {
        lum: luminance(rgbFont),
        hex: rgbToHex(rgbFont),
        rgb: rgbFont,
      };
    } else if (backgroundColor) {
      if (backgroundColor.lum < 0.5) {
        return lightColor;
      }
    }
    return darkColor;
  }

  // Calculate color scheme based on number of colors and color values
  function colorScheme(colorArray) {
    const colors = {
      background: '',
      foreground: '',
      accent: '',
    };

    if (colorArray.length === 0) {
      return new Error('Not enough colors to create popup');
    } else if (colorArray.length === 1) {
      const newColor = getComplimentColor(colorArray[0].rgb);
      colorArray.push(newColor);

      if (colorArray[0].lum < 0.5) {
        colorArray.push(lightColor);
      } else {
        colorArray.push(darkColor);
      }
    } else if (colorArray.length === 2) {
      // Try to use one of the compliment colors from the existing colors
      const possibleColorOne = getComplimentColor(colorArray[0].rgb);
      const possibleColorTwo = getComplimentColor(colorArray[1].rgb);
      const colorOneDiff = calculateColorDiff(colorArray[1], possibleColorOne);
      const colorTwoDiff = calculateColorDiff(colorArray[1], possibleColorTwo);

      if (colorOneDiff.hex !== lightColor.hex && colorOneDiff.hex !== darkColor.hex) {
        colors.accent = colorOneDiff;
        colorArray.push(colorOneDiff);
      } else {
        colors.accent = colorTwoDiff;
        colorArray.push(colorTwoDiff);
      }
    }

    if (colorArray[0].lum < colorArray[1].lum) {
      colors.background = colorArray[1];
      colors.foreground = calculateColorDiff(colorArray[0], colorArray[1]);
      colors.accent = calculateColorDiff(colorArray[2], colorArray[1]);
    } else {
      colors.background = colorArray[0];
      colors.foreground = calculateColorDiff(colorArray[1], colorArray[0]);
      colors.accent = calculateColorDiff(colorArray[2], colorArray[0]);
    }

    return colors;
  }

  // Update styles to template
  function updateStyles(component, rgbColors, borderRadius, fontFamily, fontColor) {
    const { style, type, action } = component;

    if (type === 'background') {
      style.backgroundColor = formatColorRGBA(rgbColors.background);
    } else if (type === 'textField') {
      if (rgbColors.background.lum > 0.5) {
        if (contrast(1, rgbColors.background.lum) < 1.2) {
          style.backgroundColor = formatColorRGBA(darkColor);
          style.color = formatColorRGBA(lightColor);
        }
      }
    } else if (type === 'button' && (action === 'redirect' || action === 'close' || action === 'push' || action === 'optout')) {
      style.backgroundColor = formatColorRGBA(rgbColors.accent);
      style.color = formatColorRGBA(correctedFontColor(rgbColors.foreground, rgbColors.accent));
    } else if (type === 'button') { // Default
      style.backgroundColor = formatColorRGBA(rgbColors.foreground);
      style.color = formatColorRGBA(correctedFontColor(rgbColors.accent, rgbColors.foreground));
    } else if (action === 'close') {
      if (type === 'image') {
        style.fill = formatColorRGBA(rgbColors.accent);
      } else {
        style.backgroundColor = formatColorRGBA(rgbColors.foreground);
      }
    } else if (type === 'text') {
      style.fontFamily = fontFamily;
      style.color = formatColorRGBA(correctedFontColor(fontColor, rgbColors.background));
    }

    if (type !== 'text' || type !== 'image') {
      style.borderBottomLeftRadius = borderRadius;
      style.borderBottomRightRadius = borderRadius;
      style.borderTopLeftRadius = borderRadius;
      style.borderTopRightRadius = borderRadius;
      style.borderRadius = borderRadius;
    }
  }

  function createScrapedPopupJson(template, scrapedData, siteId, sumoUrl, formType) {
    return new Promise((resolve) => {
      const fontFamily = scrapedData.fontFamily || "'Open Sans',sans-serif";
      const borderRadius = scrapedData.borderRadius || '3px';
      const templateJson = JSON.parse(template);
      const backgroundColors = backgroundColorValues(scrapedData);
      const rgbColorScheme = colorScheme(backgroundColors);
      const fontColor = fontColorValue(scrapedData.fontColor, rgbColorScheme.background);

      if (rgbColorScheme instanceof Error) {
        resolve([template, { message: rgbColorScheme.toString() }]);
      }

      // Find and change elements for each type of popup (if present)
      if (formType === 'welcome-mat') {
        // Try/Catch in case new type of template is added
        try {
          const welcomeMatObj =
            templateJson.subscribe.components[0].childComponents[0].childComponents;
          welcomeMatObj[1].style.backgroundColor = formatColorRGBA(rgbColorScheme.background);
          welcomeMatObj[0].childComponents.forEach((val) => {
            updateStyles(val, rgbColorScheme, borderRadius, fontFamily, fontColor);
          });
        } catch (e) {
          resolve([template, { message: 'Cannot create Welcome Mat Style' }]);
        }
      } else {
        templateJson.steps.forEach((type) => {
          templateJson[type].components.forEach((val) => {
            updateStyles(val, rgbColorScheme, borderRadius, fontFamily, fontColor);
          });
        });
      }
      resolve([JSON.stringify(templateJson), false]);
    });
  }

  return {
    createScrapedPopupJson,
    hexToRGB,
    luminance,
  };
});
